import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Leaf,
  BookOpen,
  Sparkles,
  Search,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
} from "lucide-react";
import { IridologyFormData, IridologyRecommendation } from "@/types/iridology";

interface IridologyRecommendationsProps {
  formData: IridologyFormData;
  sessionId: string;
  recommendations: IridologyRecommendation[];
  onRecommendationsChange: (recommendations: IridologyRecommendation[]) => void;
}

interface MedicinalPlant {
  id: string;
  latin_name: string;
  common_names: string[];
  main_effects: string[] | null;
  description: string;
}

interface Treatment {
  id: string;
  title: string;
  detail: string;
  treatment_type: string;
  symptom_keywords: string[];
  posology: string | null;
}

interface BachFlower {
  id: string;
  name: string;
  description: string;
  main_effects: string[];
  symptoms_keywords: string[];
  usage_protocol: string;
}

const normalize = (text: string) =>
  (text || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Summary component for category counters and filters
const RecommendationsSummary: React.FC<{
  recommendations: IridologyRecommendation[];
  categoryFilter: string;
  onCategoryFilterChange: (filter: string) => void;
}> = ({ recommendations, categoryFilter, onCategoryFilterChange }) => {
  const plantCount = recommendations.filter(r => r.category === "Plante médicinale").length;
  const treatmentCount = recommendations.filter(r => 
    r.category !== "Plante médicinale" && r.category !== "Fleurs de Bach"
  ).length;
  const bachCount = recommendations.filter(r => r.category === "Fleurs de Bach").length;

  const filters = [
    { key: "all", label: "Tout", count: recommendations.length, icon: Sparkles, color: "bg-primary/10 text-primary" },
    { key: "plant", label: "Plantes", count: plantCount, icon: Leaf, color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
    { key: "treatment", label: "Traitements", count: treatmentCount, icon: BookOpen, color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
    { key: "bach", label: "Fleurs Bach", count: bachCount, icon: Sparkles, color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {filters.map((filter) => {
        const Icon = filter.icon;
        const isActive = categoryFilter === filter.key;
        return (
          <button
            key={filter.key}
            onClick={() => onCategoryFilterChange(isActive ? "all" : filter.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              isActive 
                ? `${filter.color} ring-2 ring-offset-1 ring-current` 
                : `${filter.color} opacity-60 hover:opacity-100`
            }`}
          >
            <Icon className="w-3 h-3" />
            <span>{filter.label}</span>
            <Badge variant="secondary" className="h-5 w-5 p-0 flex items-center justify-center text-[10px]">
              {filter.count}
            </Badge>
          </button>
        );
      })}
    </div>
  );
};

export const IridologyRecommendations: React.FC<IridologyRecommendationsProps> = ({
  formData,
  sessionId,
  recommendations,
  onRecommendationsChange,
}) => {
  const [plants, setPlants] = useState<MedicinalPlant[]>([]);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [bachFlowers, setBachFlowers] = useState<BachFlower[]>([]);
  const [searchPlant, setSearchPlant] = useState("");
  const [searchTreatment, setSearchTreatment] = useState("");
  const [searchBach, setSearchBach] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  // Filter recommendations based on category
  const filteredRecommendations = categoryFilter === "all" 
    ? recommendations 
    : recommendations.filter(r => {
        if (categoryFilter === "plant") return r.category === "Plante médicinale";
        if (categoryFilter === "bach") return r.category === "Fleurs de Bach";
        if (categoryFilter === "treatment") return r.category !== "Plante médicinale" && r.category !== "Fleurs de Bach";
        return true;
      });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [{ data: plantsData, error: plantsError }, { data: treatmentsData, error: treatmentsError }, { data: bachData, error: bachError }] =
        await Promise.all([
          supabase
            .from("medicinal_plants")
            .select("id, latin_name, common_names, main_effects, description, keywords"),
          // RLS limite déjà au praticien (auth.uid() = user_id) → ne pas filtrer côté client avec un userId vide.
          supabase
            .from("practitioner_treatments")
            .select("id, title, detail, treatment_type, symptom_keywords, posology, is_active")
            .eq("is_active", true),
          supabase
            .from("bach_flowers")
            .select("id, name, description, main_effects, symptoms_keywords, usage_protocol"),
        ]);

      if (plantsError) throw plantsError;
      if (treatmentsError) throw treatmentsError;
      if (bachError) throw bachError;

      if (plantsData) setPlants(plantsData as any);
      if (treatmentsData) setTreatments(treatmentsData as any);
      if (bachData) setBachFlowers(bachData as any);
    } catch (error) {
      console.error("Erreur chargement données:", error);
      // Ne pas spammer : simple toast si l'utilisateur est bien connecté mais le select échoue.
      toast.error("Impossible de charger les bibliothèques de recommandations");
    } finally {
      setLoading(false);
    }
  };

  // Generate keywords based on iridology analysis
  const getAnalysisKeywords = (): string[] => {
    const keywords: string[] = [];

    // Terrains
    if (formData.terrain_nervous) keywords.push("nerveux", "stress", "anxiété", "sommeil", "relaxation", "calme", "nervosité");
    if (formData.terrain_digestive) keywords.push("digestif", "digestion", "foie", "intestin", "estomac", "hépatique", "bile", "transit");
    if (formData.terrain_circulatory) keywords.push("circulation", "veineux", "cardiovasculaire", "sang", "veines", "artères", "jambes lourdes");
    if (formData.terrain_eliminatory) keywords.push("élimination", "détox", "drainage", "reins", "toxines", "purification", "dépuratif");
    if (formData.terrain_mixed) keywords.push("terrain", "constitution");

    // Surcharges
    if (formData.overload_digestive) keywords.push("digestion", "hépatique", "bile", "foie", "intestin", "constipation", "ballonnements");
    if (formData.overload_nervous) keywords.push("nervosité", "calme", "relaxation", "anxiété", "stress", "tension");
    if (formData.overload_emotional) keywords.push("émotions", "équilibre émotionnel", "humeur", "moral");
    if (formData.overload_eliminatory) keywords.push("toxines", "purification", "drainage", "reins", "peau");
    if (formData.overload_circulatory) keywords.push("sang", "veines", "circulation", "jambes");

    // Vitalité
    if (formData.vitality_level === "needs_support") keywords.push("énergie", "vitalité", "tonique", "fatigue", "asthénie", "revitalisant");
    if (formData.vitality_level === "average") keywords.push("équilibre", "soutien", "maintien");

    // Adaptation
    if (formData.adaptation_stress_sensitivity) keywords.push("adaptogène", "stress", "résistance", "cortisol", "nerveux");
    if (formData.adaptation_fatigue) keywords.push("fatigue", "tonique", "énergie", "dynamisant", "asthénie");
    if (formData.adaptation_good) keywords.push("maintien", "prévention", "équilibre");

    // Normalisation (accents, doublons)
    const unique = new Map<string, string>();
    keywords.forEach((k) => {
      const nk = normalize(k).trim();
      if (nk.length >= 3 && !unique.has(nk)) unique.set(nk, k);
    });
    return [...unique.values()];
  };

  const analysisKeywords = getAnalysisKeywords();
  const normalizedKeywords = analysisKeywords.map((k) => normalize(k));

  // Filter plants based on analysis
  const getSuggestedPlants = (): MedicinalPlant[] => {
    if (plants.length === 0) return [];
    if (analysisKeywords.length === 0) return plants.slice(0, 10);

    const scored = plants.map((plant) => {
      const searchText = normalize([
        ...plant.common_names,
        plant.latin_name,
        plant.description,
        ...(plant.main_effects || []),
      ].join(" "));

      const score = normalizedKeywords.reduce((acc, kw) => (searchText.includes(kw) ? acc + 1 : acc), 0);
      return { plant, score };
    });

    return scored
      .sort((a, b) => b.score - a.score)
      .map((s) => s.plant)
      .slice(0, 15);
  };

  // Filter treatments based on analysis (accent-insensitive + fallback)
  const getSuggestedTreatments = (): Treatment[] => {
    if (treatments.length === 0) return [];
    if (analysisKeywords.length === 0) return treatments.slice(0, 10);

    const scored = treatments.map((treatment) => {
      const text = normalize([
        treatment.title,
        treatment.detail,
        treatment.treatment_type,
        treatment.posology || "",
        ...(treatment.symptom_keywords || []),
      ].join(" "));

      // pondération: match dans symptom_keywords plus important
      const keywordsText = normalize((treatment.symptom_keywords || []).join(" "));
      const score = normalizedKeywords.reduce((acc, kw) => {
        const inKeywords = keywordsText.includes(kw);
        const inText = text.includes(kw);
        return acc + (inKeywords ? 3 : 0) + (inText ? 1 : 0);
      }, 0);

      return { treatment, score };
    });

    const top = scored.sort((a, b) => b.score - a.score).map((s) => s.treatment).slice(0, 15);
    return top;
  };

  const getSuggestedBach = (): BachFlower[] => {
    if (bachFlowers.length === 0) return [];
    if (analysisKeywords.length === 0) return bachFlowers.slice(0, 10);

    const scored = bachFlowers.map((flower) => {
      const text = normalize([
        flower.name,
        flower.description,
        ...(flower.main_effects || []),
        ...(flower.symptoms_keywords || []),
      ].join(" "));

      const keywordsText = normalize((flower.symptoms_keywords || []).join(" "));
      const score = normalizedKeywords.reduce((acc, kw) => {
        const inKeywords = keywordsText.includes(kw);
        const inText = text.includes(kw);
        return acc + (inKeywords ? 3 : 0) + (inText ? 1 : 0);
      }, 0);

      return { flower, score };
    });

    return scored
      .sort((a, b) => b.score - a.score)
      .map((s) => s.flower)
      .slice(0, 15);
  };

  // Search filtering
  const filteredPlants = searchPlant
    ? plants.filter((p) => {
        const q = normalize(searchPlant);
        return normalize(p.latin_name).includes(q) || normalize(p.common_names.join(" ")).includes(q);
      })
    : getSuggestedPlants();

  const filteredTreatments = searchTreatment
    ? treatments.filter((t) => {
        const q = normalize(searchTreatment);
        return (
          normalize(t.title).includes(q) ||
          normalize(t.detail).includes(q) ||
          normalize((t.symptom_keywords || []).join(" ")).includes(q)
        );
      })
    : getSuggestedTreatments();

  const filteredBach = searchBach
    ? bachFlowers.filter((f) => {
        const q = normalize(searchBach);
        return (
          normalize(f.name).includes(q) ||
          normalize(f.description).includes(q) ||
          normalize((f.symptoms_keywords || []).join(" ")).includes(q)
        );
      })
    : getSuggestedBach();

  const addRecommendation = (type: "plant" | "treatment" | "bach", item: MedicinalPlant | Treatment | BachFlower) => {
    const treatment = item as Treatment;
    const plant = item as MedicinalPlant;
    const flower = item as BachFlower;

    let description = "";
    if (type === "plant") {
      description = plant.main_effects?.join(", ") || plant.description.substring(0, 150);
    } else if (type === "treatment") {
      description = treatment.detail.substring(0, 200);
      if (treatment.posology) {
        description += ` | Posologie: ${treatment.posology}`;
      }
    } else {
      description = flower.description.substring(0, 220);
      if (flower.usage_protocol) {
        description += ` | Utilisation: ${flower.usage_protocol.substring(0, 120)}`;
      }
    }

    const newRec: IridologyRecommendation = {
      id: `${type}_${(item as any).id}_${Date.now()}`,
      category:
        type === "plant"
          ? "Plante médicinale"
          : type === "treatment"
            ? treatment.treatment_type || "Traitement"
            : "Fleurs de Bach",
      title: type === "plant" ? plant.common_names[0] : type === "treatment" ? treatment.title : flower.name,
      description,
      priority: recommendations.length + 1,
    };

    onRecommendationsChange([...recommendations, newRec]);
    toast.success(`${newRec.title} ajouté aux recommandations`);
  };

  const removeRecommendation = (id: string) => {
    onRecommendationsChange(recommendations.filter((r) => r.id !== id));
  };

  const moveRecommendation = (id: string, direction: "up" | "down") => {
    const index = recommendations.findIndex((r) => r.id === id);
    if (index === -1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= recommendations.length) return;

    const newRecs = [...recommendations];
    [newRecs[index], newRecs[newIndex]] = [newRecs[newIndex], newRecs[index]];
    newRecs.forEach((r, i) => (r.priority = i + 1));
    onRecommendationsChange(newRecs);
  };

  const saveRecommendations = async () => {
    setSaving(true);
    try {
      // Convert to plain JSON for Supabase
      const recsJson = recommendations.map((r) => ({
        id: r.id,
        category: r.category,
        title: r.title,
        description: r.description,
        priority: r.priority,
      }));

      const { error } = await supabase
        .from("iridology_sessions")
        .update({ recommendations: recsJson as unknown as any })
        .eq("id", sessionId);

      if (error) throw error;
      toast.success("Recommandations enregistrées");
    } catch (error: any) {
      console.error("Erreur sauvegarde:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const isItemSelected = (itemId: string) => {
    return recommendations.some((r) => r.id.includes(itemId));
  };

  return (
    <div className="space-y-6">
      {/* Analysis Keywords */}
      {analysisKeywords.length > 0 && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Mots-clés issus de l'analyse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {analysisKeywords.map((keyword, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {keyword}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Recommandations sélectionnées</CardTitle>
              <CardDescription>{recommendations.length} recommandation(s)</CardDescription>
            </div>
            {recommendations.length > 0 && (
              <Button size="sm" onClick={saveRecommendations} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {recommendations.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Leaf className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Aucune recommandation sélectionnée</p>
              <p className="text-xs mt-1">
                Utilisez les onglets ci-dessous pour ajouter des plantes, traitements ou fleurs de Bach
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {recommendations.map((rec, index) => (
                <div key={rec.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg group">
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => moveRecommendation(rec.id, "up")}
                      disabled={index === 0}
                    >
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => moveRecommendation(rec.id, "down")}
                      disabled={index === recommendations.length - 1}
                    >
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">
                        {rec.category}
                      </Badge>
                      <span className="font-medium text-sm">{rec.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">{rec.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 opacity-0 group-hover:opacity-100 text-destructive"
                    onClick={() => removeRecommendation(rec.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Suggestions Tabs */}
      <Tabs defaultValue="plants" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="plants" className="gap-2">
            <Leaf className="w-4 h-4" />
            Plantes médicinales
          </TabsTrigger>
          <TabsTrigger value="treatments" className="gap-2">
            <BookOpen className="w-4 h-4" />
            Bibliothèque traitements
          </TabsTrigger>
          <TabsTrigger value="bach" className="gap-2">
            <Sparkles className="w-4 h-4" />
            Fleurs de Bach
          </TabsTrigger>
        </TabsList>

        {/* PLANTS */}
        <TabsContent value="plants" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher une plante..."
                  value={searchPlant}
                  onChange={(e) => setSearchPlant(e.target.value)}
                  className="pl-9"
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-6 text-muted-foreground">Chargement...</div>
              ) : filteredPlants.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <p className="text-sm">Aucune plante trouvée</p>
                </div>
              ) : (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {filteredPlants.map((plant) => (
                      <div
                        key={plant.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                          isItemSelected(plant.id) ? "bg-primary/10 border-primary/30" : "hover:bg-muted/50"
                        }`}
                      >
                        <Checkbox
                          checked={isItemSelected(plant.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              addRecommendation("plant", plant);
                            } else {
                              const recId = recommendations.find((r) => r.id.includes(plant.id))?.id;
                              if (recId) removeRecommendation(recId);
                            }
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{plant.common_names[0]}</div>
                          <div className="text-xs text-muted-foreground italic">{plant.latin_name}</div>
                          {plant.main_effects && plant.main_effects.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {plant.main_effects.slice(0, 3).map((effect, i) => (
                                <Badge key={i} variant="secondary" className="text-[10px]">
                                  {effect}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => addRecommendation("plant", plant)}
                          disabled={isItemSelected(plant.id)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TREATMENTS */}
        <TabsContent value="treatments" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un traitement..."
                  value={searchTreatment}
                  onChange={(e) => setSearchTreatment(e.target.value)}
                  className="pl-9"
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-6 text-muted-foreground">Chargement...</div>
              ) : filteredTreatments.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <p className="text-sm">Aucun traitement trouvé</p>
                  <p className="text-xs mt-1">Ajoutez des traitements dans la bibliothèque</p>
                </div>
              ) : (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {filteredTreatments.map((treatment) => (
                      <div
                        key={treatment.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                          isItemSelected(treatment.id) ? "bg-primary/10 border-primary/30" : "hover:bg-muted/50"
                        }`}
                      >
                        <Checkbox
                          checked={isItemSelected(treatment.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              addRecommendation("treatment", treatment);
                            } else {
                              const recId = recommendations.find((r) => r.id.includes(treatment.id))?.id;
                              if (recId) removeRecommendation(recId);
                            }
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">
                              {treatment.treatment_type}
                            </Badge>
                            <span className="font-medium text-sm">{treatment.title}</span>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{treatment.detail}</p>
                          {treatment.posology && (
                            <p className="text-xs text-primary mt-1">Posologie: {treatment.posology}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => addRecommendation("treatment", treatment)}
                          disabled={isItemSelected(treatment.id)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* BACH FLOWERS */}
        <TabsContent value="bach" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher une fleur de Bach..."
                  value={searchBach}
                  onChange={(e) => setSearchBach(e.target.value)}
                  className="pl-9"
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-6 text-muted-foreground">Chargement...</div>
              ) : filteredBach.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <p className="text-sm">Aucune fleur de Bach trouvée</p>
                </div>
              ) : (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {filteredBach.map((flower) => (
                      <div
                        key={flower.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                          isItemSelected(flower.id) ? "bg-primary/10 border-primary/30" : "hover:bg-muted/50"
                        }`}
                      >
                        <Checkbox
                          checked={isItemSelected(flower.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              addRecommendation("bach", flower);
                            } else {
                              const recId = recommendations.find((r) => r.id.includes(flower.id))?.id;
                              if (recId) removeRecommendation(recId);
                            }
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{flower.name}</div>
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{flower.description}</p>
                          {flower.main_effects?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {flower.main_effects.slice(0, 3).map((effect, i) => (
                                <Badge key={i} variant="secondary" className="text-[10px]">
                                  {effect}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => addRecommendation("bach", flower)}
                          disabled={isItemSelected(flower.id)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
